import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { DimensionValue, Kpi } from './Kpi';
import { IYearMonth } from '@/coreDomainsModule/date';

/**
 * ダッシュボードの情報定義
 */
export interface DashboardInformation {
  /** 分析するデータ種類 */
  metricType: MetricType;
  /** 分析の切り口種類 */
  dimensionType: DimensionType;
  /** 各切り口の値ごとにグループ化した内容 */
  dimensionValueGroups: DimensionValueGroup[];
}

/**
 * 切り口ごとの年月別サマリ値
 */
export interface SummaryAmountsByDimension {
  yearMonth: IYearMonth;

  arrival?: number;
  endInventory?: number;
  orderAmount?: number;
  supplierForecast?: number;
  turnover?: number;
  averageForecastAccuracy?: number;
  turnoverWithForecastAccuracy?: number;

  customerForecast?: number;
  forecastAccuracy?: number;
  salesResult?: number;
}

/**
 * 各項目ごとに出す情報
 */
export interface DimensionValueGroup {
  dimensionValues: DimensionValue;
  kpi: Kpi[];
  businesses: (IBusiness & {
    endInventory?: number;
    salesResult?: number;
    customerForecast?: number;
    arrival?: number;
    turnover?: number;
    forecastAccuracy?: number;
    supplierForecast?: number;
    orderAmount?: number;
  })[];
  amounts: SummaryAmountsByDimension[];
}

/**
 * 分析するデータ種類の一覧
 */
export const MetricTypesByAggbiz = {
  Turnover: 'TURNOVER',
  Inventory: 'END_INVENTORY',
  Arrival: 'ARRIVAL',
  OrderAmount: 'ORDER_AMOUNT',
  SupplierForecast: 'SUPPLIER_FORECAST',
} as const;

export const MetricTypesByBaseBiz = {
  SalesResult: 'SALES_RESULT',
  CustomerForecast: 'CUSTOMER_FORECAST',
  ForecastAccuracy: 'FORECAST_ACCURACY',
};

export const MetricTypes = { ...MetricTypesByAggbiz, ...MetricTypesByBaseBiz, UNKNOWN: 'METRIC_UNKNOWN' };

/**
 * 分析するデータ種類のタイプ
 */
export type MetricTypeByAggBiz = (typeof MetricTypesByAggbiz)[keyof typeof MetricTypesByAggbiz];
/**
 * 分析するデータ種類のタイプ
 */
export type MetricTypeByBaseBiz = (typeof MetricTypesByBaseBiz)[keyof typeof MetricTypesByBaseBiz];
/**
 * 分析するデータ種類のタイプ
 */
export type MetricType = (typeof MetricTypes)[keyof typeof MetricTypes];

export const metricTypesToSummaryAmountsKeyMapper = {
  [MetricTypes.Turnover]: 'turnover',
  [MetricTypes.Inventory]: 'endInventory',
  [MetricTypes.CustomerForecast]: 'customerForecast',
  [MetricTypes.SalesResult]: 'salesResult',
  [MetricTypes.Arrival]: 'arrival',
  [MetricTypes.ForecastAccuracy]: 'forecastAccuracy',
  [MetricTypes.OrderAmount]: 'orderAmount',
  [MetricTypes.SupplierForecast]: 'supplierForecast',
};

/**
 * グループ化して表示する集計項目の一覧
 */
export const DimensionTypesByAggBiz = {
  AggregateBusinessResponsibleUser: 'aggregateBusinessResponsibleUser',
  Warehouse: 'warehouse',
  Supplier: 'supplier',
  Business: 'business',
  Package: 'package',
  Manufacturer: 'manufacturer',
  OrderPackage: 'orderPackage',
  Moq: 'moq',
  Unit: 'unit',
  Group: 'group',
  LeadTime: 'leadTime',
  StdInventoryIndexMin: 'stdInventoryIndexMin',
  StdInventoryIndexMax: 'stdInventoryIndexMax',
  UserSpecificNumber: 'userSpecificNumber',
} as const;

/**
 * グループ化して表示する取引項目の一覧
 */
export const DimensionTypesByBaseBiz = {
  BaseBusinessResponsibleUser: 'baseBusinessResponsibleUser',
  Customer: 'customer',
  SalesDestination: 'salesDestination',
  EndUser: 'endUser',
} as const;

export const DimensionTypes = { ...DimensionTypesByAggBiz, ...DimensionTypesByBaseBiz, UNKNOWN: 'DIMENSION_UNKNOWN' };

/**
 * グループ化して表示する項目のタイプ
 */
export type DimensionTypeByAggBiz = (typeof DimensionTypesByAggBiz)[keyof typeof DimensionTypesByAggBiz];

/**
 * グループ化して表示する項目のタイプ
 */
export type DimensionTypeByBaseBiz = (typeof DimensionTypesByBaseBiz)[keyof typeof DimensionTypesByBaseBiz];

/**
 * グループ化して表示する項目のタイプ
 */
export type DimensionType = (typeof DimensionTypes)[keyof typeof DimensionTypes];
